import { IMPORT_STAGES, type ImportOrderResponse, type ImportStage } from '@palaccount/contracts';

/** The seven stages of the rail — every stage but the terminal `cancelled`. */
export type RailStage = (typeof IMPORT_STAGES)[number];

/**
 * PUR-06 §7's stage machine, on the client side only so the screen can grey
 * out what the server would refuse anyway. The server remains the authority —
 * nothing here is a permission check, and every one of these rules is enforced
 * again in imports.service.ts.
 */

/** A stage's place on the rail; -1 for `cancelled`, which is off it (wave 5E). */
export function stageIndex(stage: ImportStage): number {
  return IMPORT_STAGES.indexOf(stage as RailStage);
}

export function nextStage(stage: ImportStage): RailStage | null {
  const at = stageIndex(stage);
  return at >= 0 && at < IMPORT_STAGES.length - 1 ? IMPORT_STAGES[at + 1]! : null;
}

export function previousStage(stage: ImportStage): RailStage | null {
  const at = stageIndex(stage);
  return at > 0 ? IMPORT_STAGES[at - 1]! : null;
}

/**
 * BR-PUR-06-10 — the receiving card exists from customs clearance onward, and
 * not one stage earlier. A shipment still on the water has nothing on a shelf
 * to count, so offering the panel would be offering a mistake.
 */
export function receivingOpen(stage: ImportStage): boolean {
  return stageIndex(stage) >= stageIndex('customs_clearance');
}

/**
 * BR-PUR-06-20, BOTH of its clauses — "A shipment must not be moved backward
 * without an import coordinator and a typed reason, and not at all once goods
 * have been received." The first clause is the caller's (it collects the
 * reason); this predicate is the second, plus the floor at stage 0.
 *
 * The header here previously named BR-PUR-06-21 (rule-coverage:ignore) for
 * the shelf clause. That id is the Incoterm-meanings rule and has nothing to
 * do with stage movement; the mistake had it counting as covered while the
 * screen still prints bare codes. Corrected rather than carried, and marked
 * so naming it here does not repeat the over-count in a new place.
 *
 * ERR-PUR-06-08 is the §14 row this closes: "Advanced a stage by mistake |
 * (nothing — there is no way back) | This is the gap in BR-PUR-06-20." A row
 * with no message, describing the prototype's one-way rail. There is a way
 * back now — bounded by this predicate on the client and enforced again in
 * imports.service.ts, which is the authority.
 */
export function canGoBack(stage: ImportStage): boolean {
  return stageIndex(stage) > 0 && stageIndex(stage) < stageIndex('received');
}

/** A closed shipment takes no edits at all — ERR-PUR-06-07; nor (wave 5E) a cancelled one. */
export function isEditable(stage: ImportStage): boolean {
  return stage !== 'closed' && stage !== 'cancelled';
}

/**
 * Wave 5E — the Cancel act is offered from any stage BEFORE Received, and
 * only while nothing has been received (a part-received shipment at customs
 * has stock on the shelf). The server refuses the rest with
 * `IMPORT_REFUSALS.cancelAfterReceipt`; this only keeps the button honest.
 */
export function canCancel(order: Pick<ImportOrderResponse, 'stage' | 'receiving'>): boolean {
  const at = stageIndex(order.stage);
  return at >= 0 && at < stageIndex('received') && order.receiving.state === 'none';
}

/**
 * §5f's badges. "Partial" and "All received" are mutually exclusive — the
 * design note calls out that they must never show together, which is why this
 * returns one value rather than two booleans.
 */
export function receiptState(ordered: string, received: string): 'none' | 'partial' | 'all' {
  const orderedQty = Number(ordered);
  const receivedQty = Number(received);
  if (receivedQty <= 0) return 'none';
  return receivedQty >= orderedQty ? 'all' : 'partial';
}

/**
 * ERR-PUR-06-05's guard, restated on the client so the number turns red as it
 * is typed rather than after a round trip. Returns the offending line, or null.
 */
export function overReceived<T extends { qty: string; receivedQty: string }>(
  lines: T[],
  edits: Record<string, string>,
  idOf: (line: T) => string,
): T | null {
  for (const line of lines) {
    const typed = edits[idOf(line)];
    const attempted = typed === undefined || typed === '' ? Number(line.receivedQty) : Number(typed);
    if (Number.isFinite(attempted) && attempted > Number(line.qty)) return line;
  }
  return null;
}

/**
 * BR-PUR-06-14: "A shipment must not advance past Received until every line
 * is fully received" — closing needs every unit accounted for, or a recorded
 * shortfall. Enforced again server-side in imports.service.ts, which is where
 * the message lives.
 *
 * Was labelled BR-PUR-06-16 (rule-coverage:ignore) here. That id is the
 * exchange-rate-policy rule — "which rate is authoritative for the landed
 * cost: the order date, or the payment date" — and nothing in this file has
 * anything to do with it. The marked mention keeps the correction visible
 * without re-counting an unenforced rule as covered.
 */
export function outstandingUnits(lines: { qty: string; receivedQty: string }[]): number {
  return lines.reduce((sum, line) => sum + Math.max(0, Number(line.qty) - Number(line.receivedQty)), 0);
}

/**
 * What arrived in THIS delivery. Ruling R7 makes the received box a running
 * cumulative total, so the units that need identifying (BR-MD-03-17) are the
 * difference between what is typed and what was already booked in — never the
 * whole number, which would ask for last month's serials all over again.
 *
 * Returns a decimal string because that is what the capture model reads;
 * empty when nothing new has been typed, which asks for nothing.
 */
export function deltaQty(alreadyReceived: string, typed: string): string {
  const before = Number(alreadyReceived);
  const now = Number(typed);
  if (!Number.isFinite(before) || !Number.isFinite(now)) return '';
  const delta = now - before;
  return delta > 0 ? String(delta) : '';
}
